import { useEffect, useState } from 'react';
import { tieneModulo } from './negocio';
import { useNegocio } from './vocabulario';
import { Buscable } from './Buscable';
import { opcionDeInsumo } from './busqueda';
import { cuantoFalta, fechaDeCalendario } from './vencimiento';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  almacen,
  newOperationId,
  type Production,
  type PurchaseOrder,
  type Supplier,
  type ExtraRecipe,
  type Supply,
  esReventa,
} from './api';
import {
  Boton,
  Fallo,
  Filtro,
  Modal,
  Pastilla,
  Rotulo,
} from './componentes';
import { cantidad, contados, diaDeLima, porcentaje, soles } from './formato';
import {
  aMilesimas,
  avisoDeLinea,
  costoDeLinea,
  costoDeReceta,
  costoUnitario,
  enUnidad,
  foodCost,
  milesimasDeStock,
  parecidos,
  plegar,
  porcionesPosibles,
  textoDeStock,
  unidadesDeCocina,
  unidadPreferida,
  type UnidadDeCocina,
} from './receta';
import {
  EntradaInvalida,
  costoPorUnidadDeStock,
  importe,
  leerCantidad,
  numeroParaWhatsapp,
} from './numeros';
import { mayuscula, useVocabulario } from './vocabulario';

/**
 * Los formularios de alta.
 *
 * Van juntos porque comparten la misma forma —abrir encima, rellenar, guardar e
 * invalidar lo que haya en pantalla— y separarlos en cuatro archivos repetiría
 * esa mecánica cuatro veces.
 */

/**
 * El identificador de una operación, el mismo en cada intento.
 *
 * Uno nuevo por pulsación convertía un reintento tras un corte en una
 * segunda devolución o un segundo traspaso. Se renueva solo al terminar bien.
 */
function usarOperacion(): [string, () => void] {
  const [id, setId] = useState(newOperationId);
  return [id, () => setId(newOperationId())];
}

/** Un botón que abre su formulario. Es el patrón de las cuatro pantallas. */
function usarAlta(onGuardado: () => void) {
  const clientes = useQueryClient();
  const [abierto, setAbierto] = useState(false);

  return {
    abierto,
    abrir: () => setAbierto(true),
    cerrar: () => setAbierto(false),
    guardado: () => {
      setAbierto(false);
      void clientes.invalidateQueries();
      onGuardado();
    },
  };
}

// ── Insumo ────────────────────────────────────────────────────────────────

/**
 * La ficha de un insumo: sirve para darlo de alta y para corregirlo.
 *
 * Es el mismo formulario porque son los mismos datos. El backend lo trata
 * igual —el `PUT` guarda por identificador, exista o no— y tenía desde el
 * primer día la regla de que un insumo con movimientos no cambia de unidad;
 * lo único que faltaba era una puerta para entrar a editarlo. Sin ella, un
 * código mal tecleado o un mínimo que cambia obligaban a dar de alta otro
 * insumo y dejar el viejo estorbando en todas las listas.
 */
/**
 * «+ Nueva» debajo de un desplegable: crear la categoría o la unidad que falta
 * sin salir del formulario.
 *
 * Un local nuevo no tenía dónde crearlas y el alta de insumo las pide: no
 * podía dar de alta ni el primero. Y el que ya tiene las de partida también
 * necesita la suya —«atado» de culantro, «balde» de chicha—.
 */
function NuevaOpcion({
  que,
  ejemplo,
  crear,
  onCreada,
}: {
  que: string;
  ejemplo: string;
  crear: (nombre: string) => Promise<string>;
  onCreada: (id: string) => void;
}) {
  const clientes = useQueryClient();
  const [abierta, setAbierta] = useState(false);
  const [nombre, setNombre] = useState('');

  const guardar = useMutation({
    mutationFn: () => crear(nombre.trim()),
    onSuccess: async (id) => {
      await clientes.invalidateQueries({ queryKey: ['opciones'] });
      onCreada(id);
      setNombre('');
      setAbierta(false);
    },
  });

  if (!abierta) {
    return (
      <button type="button" className="accion-fila" onClick={() => setAbierta(true)}>
        + Nueva {que}
      </button>
    );
  }

  return (
    <div className="nueva-opcion">
      <input
        aria-label={`Nombre de la nueva ${que}`}
        placeholder={ejemplo}
        value={nombre}
        autoFocus
        onChange={(e) => setNombre(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && nombre.trim() !== '') {
            e.preventDefault();
            guardar.mutate();
          }
        }}
      />
      <Boton
        variante="secundario"
        onClick={() => guardar.mutate()}
        disabled={nombre.trim() === '' || guardar.isPending}
      >
        {guardar.isPending ? 'Creando…' : 'Crear'}
      </Boton>
      {guardar.isError ? <Fallo error={guardar.error} /> : null}
    </div>
  );
}

export function FichaDeInsumo({
  insumo,
  onGuardado = () => {},
}: {
  insumo?: Supply;
  onGuardado?: () => void;
}) {
  const alta = usarAlta(onGuardado);
  const vocabulario = useVocabulario();
  const opciones = useQuery({
    queryKey: ['opciones'],
    queryFn: almacen.options,
    enabled: alta.abierto,
  });

  const editando = insumo !== undefined;

  const sku = insumo?.sku ?? '';
  const [nombre, setNombre] = useState(insumo?.name ?? '');
  const [categoryId, setCategoryId] = useState('');
  const [stockUnitId, setStockUnitId] = useState('');
  const [conLote, setConLote] = useState(insumo?.isLotTracked ?? false);
  const [conFecha, setConFecha] = useState(insumo?.requiresExpiry ?? false);
  /*
   * Cada unidad con su serie o su IMEI (módulo `serials`): el celular, la
   * laptop. Una serie es un lote de uno, así que encenderla enciende el lote.
   * Lo que va por serie no se da de alta con «lo que hay hoy»: se recibe
   * serie por serie en Recepción.
   */
  const ofreceSerie = tieneModulo(useNegocio(), 'serials');
  const [porSerie, setPorSerie] = useState(insumo?.isSerialTracked ?? false);
  const [barras, setBarras] = useState(insumo?.barcode ?? '');
  const [proveedor, setProveedor] = useState('');
  const [minimo, setMinimo] = useState(insumo?.minimum ?? '');
  const [maximo, setMaximo] = useState(insumo?.maximum ?? '');
  // Lo que nadie cuenta —el cable, la mica— no tiene mínimo, y sin mínimo no
  // avisaba nunca: se enteraban cuando el cliente lo pedía (migración 274).
  const [avisarEnCero, setAvisarEnCero] = useState(insumo?.alertAtZero ?? false);
  const sinMinimo = minimo.trim() === '' || Number(minimo.replace(',', '.')) === 0;
  const [ubicacion, setUbicacion] = useState(insumo?.location ?? '');
  // La unidad en que llega —el saco— y cuánto trae de la de stock: 50 kg.
  const [unidadDeCompra, setUnidadDeCompra] = useState('');
  // Tal cual y sin los ceros de la derecha: con el formato de pantalla, 1000
  // sería «1,000» y se volvería a leer como uno.
  const [factor, setFactor] = useState(sinCerosDeMas(insumo?.purchaseFactor ?? ''));
  const [vidaUtil, setVidaUtil] = useState(
    insumo?.shelfLifeDays === null || insumo?.shelfLifeDays === undefined ? '' : String(insumo.shelfLifeDays),
  );

  /*
   * Con mercadería en el estante no se cambia si va por lote: lo que ya había
   * quedaría sin lote, o con lotes que nadie mira, y la suma de los lotes
   * dejaría de ser el stock. El servidor lo rechaza; aquí se dice antes.
   */
  const loteBloqueado = editando && Number.parseFloat(insumo.quantity) !== 0;

  /*
   * Lo que hay en el estante el día que se da de alta.
   *
   * Sin esto, todo insumo nacía en cero y salía «agotado» hasta que alguien
   * descubría que había que ir a Recepción a meterlo. Y el otro camino que
   * parecía obvio, el conteo, lo mete a costo cero: el almacén valía S/ 0 y
   * cada receta costaba nada. Se registra como una entrada en el libro, con su
   * costo, igual que una recepción: el stock no se escribe a mano.
   */
  const [existencia, setExistencia] = useState('');
  const [costo, setCosto] = useState('');
  const [lote, setLote] = useState('');
  const [vence, setVence] = useState('');

  /*
   * El mismo identificador en cada intento. Si el insumo se guarda y la
   * existencia no, volver a pulsar no crea un segundo insumo ni mete dos
   * veces lo mismo: el alta es por identificador y la entrada, por operación.
   */
  const [supplyId, setSupplyId] = useState(() => insumo?.id ?? newOperationId());
  const [operacionInicial, setOperacionInicial] = useState(() =>
    newOperationId(),
  );

  /*
   * Donde el negocio exige el vencimiento (la botica), el lote de lo que ya
   * hay es el que trae la caja: no se numera solo (`LOT_CODE_REQUIRED`).
   */
  const ajustesDeLotes = useQuery({
    queryKey: ['ajustes-de-lotes'],
    queryFn: () => almacen.lotSettings(),
    enabled: alta.abierto && !editando,
    retry: false,
  });
  const exigeLoteDelFabricante = conLote && (ajustesDeLotes.data?.requiresLotExpiry ?? false);

  const cantidadInicial = Number.parseFloat(existencia.replace(',', '.'));
  const costoInicial = Number.parseFloat(costo.replace(',', '.'));
  const conExistencia =
    !editando && !porSerie && existencia.trim() !== '' && cantidadInicial > 0;

  /*
   * La categoría, la unidad y el proveedor llegan por nombre en la fila de la
   * tabla y por identificador en el formulario, así que se resuelven cuando
   * llegan las opciones. Sin esto, abrir «Editar» dejaba los tres
   * desplegables en «Elegir…» y guardar borraba lo que ya estaba puesto.
   */
  useEffect(() => {
    if (!editando || opciones.data === undefined) return;

    setCategoryId(
      (previo) =>
        previo ||
        (opciones.data.categories.find((c) => c.name === insumo.category)?.id ??
          ''),
    );
    setStockUnitId(
      (previo) =>
        previo ||
        (opciones.data.units.find((u) => u.code === insumo.unit)?.id ?? ''),
    );
    setProveedor(
      (previo) =>
        previo ||
        (opciones.data.suppliers.find((p) => p.name === insumo.supplier)?.id ??
          ''),
    );
    setUnidadDeCompra(
      (previo) =>
        previo ||
        (opciones.data.units.find((u) => u.code === insumo.purchaseUnit)?.id ?? ''),
    );
  }, [editando, opciones.data, insumo]);

  const guardar = useMutation({
    mutationFn: async () => {
      await almacen.saveSupply({
        supplyId,
        // Al darlo de alta el código lo pone el sistema, de su categoría.
        ...(editando ? { sku: sku.trim() } : {}),
        name: nombre.trim(),
        categoryId,
        stockUnitId,
        // Una serie es un lote de uno.
        isLotTracked: conLote || porSerie,
        // A granel no hay dónde guardar una fecha.
        requiresExpiry: conLote && conFecha,
        barcode: barras.trim() === '' ? null : barras.trim(),
        preferredSupplierId: proveedor === '' ? null : proveedor,
        ...(minimo.trim() === '' ? {} : { minimum: minimo }),
        ...(maximo.trim() === '' ? {} : { maximum: maximo }),
        location: ubicacion.trim() === '' ? null : ubicacion.trim(),
        shelfLifeDays: diasDeVida(vidaUtil),
        purchaseUnitId: unidadDeCompra === '' ? null : unidadDeCompra,
        purchaseFactor: unidadDeCompra === '' ? null : factor,
      });

      // Aparte de la ficha: el servidor lo niega con mercadería sin serie en
      // el estante, y no tiene por qué deshacer el resto de lo guardado.
      if (ofreceSerie && porSerie !== (insumo?.isSerialTracked ?? false)) {
        await almacen.setSerialTracking(supplyId, porSerie);
      }

      if (avisarEnCero !== (insumo?.alertAtZero ?? false)) {
        await almacen.setLimits(supplyId, { alertAtZero: avisarEnCero });
      }

      if (!conExistencia) return;

      await almacen.receive({
        operationId: operacionInicial,
        supplyId,
        quantity: String(cantidadInicial),
        unitCost: costoPorUnidadDeStock(costo)!,
        document: 'INVENTARIO-INICIAL',
        ...(conLote
          ? {
              ...(lote.trim() === '' ? {} : { lotCode: lote.trim() }),
              expiresAt: vence === '' ? null : vence,
            }
          : {}),
      }, (mensaje) => window.confirm(mensaje));
    },
    onSuccess: () => {
      if (!editando) {
        // Todo lo del insumo anterior. El código de barras se quedaba puesto
        // y el siguiente alta nacía con el código de otro producto.
        setNombre('');
        setBarras('');
        setMinimo('');
        setMaximo('');
        setUbicacion('');
        setVidaUtil('');
        setUnidadDeCompra('');
        setFactor('');
        setConLote(false);
        setConFecha(false);
        setPorSerie(false);
        setExistencia('');
        setCosto('');
        setLote('');
        setVence('');
        // El formulario sigue montado: el próximo alta es otro insumo, y con
        // el mismo identificador pisaría a este.
        setSupplyId(newOperationId());
        setOperacionInicial(newOperationId());
      }
      alta.guardado();
    },
  });

  /*
   * Con existencia hace falta su costo —sin él el almacén se valoriza en cero
   * y las recetas no cuestan nada— y, si va por lote, el código del lote.
   */
  const existenciaCompleta =
    !conExistencia ||
    (Number.isFinite(costoInicial) &&
      costoInicial >= 0 &&
      costo.trim() !== '' &&
      // Como viaja: sin signo y con hasta cuatro decimales.
      costoPorUnidadDeStock(costo) !== null &&
      (!exigeLoteDelFabricante || lote.trim() !== ''));

  const completo =
    nombre.trim() !== '' &&
    categoryId !== '' &&
    stockUnitId !== '' &&
    // Una unidad de compra sin lo que trae no sirve para convertir nada.
    (unidadDeCompra === '' || factor.trim() !== '') &&
    existenciaCompleta;

  const unidadElegida =
    opciones.data?.units.find((u) => u.id === stockUnitId)?.code ?? 'unidad';

  if (!alta.abierto) {
    return editando ? (
      <button type="button" className="accion-fila" onClick={alta.abrir}>
        Editar
      </button>
    ) : (
      <Boton variante="primario" onClick={alta.abrir}>
        Nuevo {vocabulario.insumo}
      </Boton>
    );
  }

  return (
    <Modal
      titulo={editando ? `Editar ${insumo.name}` : `Nuevo ${vocabulario.insumo}`}
      onCerrar={alta.cerrar}
    >
      <div className="formulario">
        <div className="formulario__fila">
          {/*
           * El código lo pone el sistema al darlo de alta: el siguiente de su
           * categoría, CAR-001, CAR-002… Tecleado a mano salían «CAR1»,
           * «car-01» y «LOMO» en el mismo catálogo. Al editar se enseña, fijo.
           */}
          {editando ? (
            <div className="campo">
              <Rotulo htmlFor="sku" ayuda={`Lo pone el sistema al dar de alta el ${vocabulario.insumo}: tres letras de su categoría y un número (CAR-001). No se cambia, para que la etiqueta del estante y el historial digan siempre lo mismo.`}>Código</Rotulo>
              <input id="sku" value={sku} readOnly />
            </div>
          ) : null}
          <div className="campo">
            <Rotulo htmlFor="nombre" ayuda={`Como lo llaman en ${vocabulario.dondeSeTrabaja} y en la factura del proveedor, para que todos lo reconozcan: ${vocabulario.ejemploDeNombre}.`}>Nombre</Rotulo>
            <input
              id="nombre"
              placeholder="Lomo fino de res"
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
            />
          </div>
        </div>

        <div className="formulario__fila">
          <div className="campo">
            <Rotulo htmlFor="categoria" ayuda="El grupo al que pertenece: Carnes, Pescados, Abarrotes… Sirve para ordenar las listas, contar por partes y armar el código.">Categoría</Rotulo>
            <select
              id="categoria"
              value={categoryId}
              onChange={(e) => setCategoryId(e.target.value)}
            >
              <option value="">Elegir…</option>
              {(opciones.data?.categories ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
            <NuevaOpcion
              que="categoría"
              ejemplo="Congelados"
              onCreada={setCategoryId}
              crear={async (nombre) => {
                const categoryId = newOperationId();
                await almacen.saveCategory({ categoryId, name: nombre });
                return categoryId;
              }}
            />
          </div>
          <div className="campo">
            <Rotulo htmlFor="unidad" ayuda="En qué se cuenta en el almacén y en qué van las recetas: kilos, litros, unidades. No se puede cambiar cuando ya tiene movimientos.">Se cuenta en</Rotulo>
            <select
              id="unidad"
              value={stockUnitId}
              onChange={(e) => setStockUnitId(e.target.value)}
            >
              <option value="">Elegir…</option>
              {(opciones.data?.units ?? []).map((u) => (
                <option key={u.id} value={u.id}>
                  {u.code} · {u.name}
                </option>
              ))}
            </select>
            <NuevaOpcion
              que="unidad"
              ejemplo="balde"
              onCreada={setStockUnitId}
              crear={async (nombre) => {
                const unitId = newOperationId();
                await almacen.saveUnit({ unitId, code: nombre.slice(0, 20), name: nombre });
                return unitId;
              }}
            />
          </div>
        </div>

        <p className="aviso-caja">
          La unidad no se puede cambiar después de que el insumo tenga
          movimientos: haría que todo su histórico dijera otra cosa sin tocar ni
          un número.
        </p>

        <div className="formulario__fila">
          <div className="campo">
            <Rotulo htmlFor="minimo" ayuda={`Por debajo de esta cantidad el ${vocabulario.insumo} sale en «Bajo mínimo» y en «Qué comprar». Pon lo que necesitas para aguantar hasta que llegue el proveedor.`}>Stock mínimo</Rotulo>
            <input
              id="minimo"
              inputMode="decimal"
              placeholder="12"
              value={minimo}
              onChange={(e) => setMinimo(e.target.value)}
            />
          </div>
          <div className="campo">
            <Rotulo htmlFor="maximo" ayuda="Hasta dónde conviene llenar. «Qué comprar» sugiere pedir lo justo para llegar aquí, sin comprar de más.">Stock máximo</Rotulo>
            <input
              id="maximo"
              inputMode="decimal"
              placeholder="30"
              value={maximo}
              onChange={(e) => setMaximo(e.target.value)}
            />
          </div>
        </div>

        <div className="formulario__fila">
          <div className="campo">
            <Rotulo htmlFor="barras" ayuda={`El número impreso bajo las barras del envase. Con él se encuentra el ${vocabulario.insumo} escaneando, al recibir y al contar. Lo que va a granel no lo necesita.`}>Código de barras</Rotulo>
            <input
              id="barras"
              inputMode="text"
              autoComplete="off"
              placeholder="7750182001234"
              value={barras}
              onChange={(e) => setBarras(e.target.value)}
            />
            <p className="nota-sm nota--pegada">
              {/*
               * Se puede rellenar con el propio lector: el campo es de texto y
               * un lector USB escribe como un teclado. Es más rápido y no se
               * teclea mal un dígito de trece.
               */}
              El impreso en el envase. Se puede leer aquí mismo con el lector.
              Lo que va a granel no lo tiene, y está bien.
            </p>
          </div>
          <div className="campo">
            <Rotulo htmlFor="ubicacion" ayuda="Dónde se guarda en el local: Cámara 1, Almacén seco, Barra… Ayuda a encontrarlo y a contar por zonas.">Dónde se guarda</Rotulo>
            <input
              id="ubicacion"
              placeholder="Cámara 1"
              value={ubicacion}
              onChange={(e) => setUbicacion(e.target.value)}
            />
          </div>
          <div className="campo">
            <Rotulo htmlFor="proveedor" ayuda="A quién se le compra normalmente. «Qué comprar» agrupa lo que falta por proveedor para mandarle un solo pedido.">Se le compra a</Rotulo>
            <Buscable
              id="proveedor"
              valor={proveedor}
              onCambio={setProveedor}
              sinEleccion="Sin proveedor fijo"
              placeholder="Busca el proveedor…"
              opciones={(opciones.data?.suppliers ?? []).map((p) => ({
                valor: p.id,
                etiqueta: p.name,
              }))}
            />
          </div>
        </div>

        <div className="formulario__fila">
          <div className="campo">
            <Rotulo htmlFor="unidad-compra" ayuda="Si llega en otra presentación que la que se cuenta —un saco, una caja—, elígela aquí. En Recepción se podrá teclear «2 sacos» y entrará en kilos.">Se compra en</Rotulo>
            <select id="unidad-compra" value={unidadDeCompra} onChange={(e) => setUnidadDeCompra(e.target.value)}>
              <option value="">La misma en que se cuenta</option>
              {(opciones.data?.units ?? [])
                .filter((u) => u.id !== stockUnitId)
                .map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.code} · {u.name}
                  </option>
                ))}
            </select>
          </div>
          {unidadDeCompra === '' ? null : (
            <div className="campo">
              <Rotulo htmlFor="factor" ayuda="Cuánto trae cada unidad de compra, en la unidad en que se cuenta. Un saco de arroz de 50 kg: 50.">Cada una trae ({unidadElegida})</Rotulo>
              <input id="factor" inputMode="decimal" placeholder="50" value={factor} onChange={(e) => setFactor(e.target.value)} />
              <p className="nota-sm nota--pegada">
                Así en Recepción se teclean «2 sacos a S/ 180» y entran 100 kg al costo del kilo.
              </p>
            </div>
          )}
        </div>

        <div className="formulario__fila">
          <div className="campo">
            <Rotulo htmlFor="vida-util" ayuda="Solo para lo que se prepara en el local (leche de tigre, fondos). Cada tanda que se produce vence a los días que pongas aquí, y avisa antes de pasarse.">Dura (días)</Rotulo>
            <input
              id="vida-util"
              inputMode="numeric"
              placeholder="Solo si se produce aquí"
              value={vidaUtil}
              onChange={(e) => setVidaUtil(e.target.value.replace(/\D/g, ''))}
            />
            <p className="nota-sm nota--pegada">
              Para lo que sale de una producción: la leche de tigre, el fondo. Cada tanda nace con
              su vencimiento a estos días. Sin él, no avisa nunca que se pasa.
            </p>
          </div>
        </div>

        {sinMinimo ? (
          <>
            <button
              type="button"
              className={`opcion ${avisarEnCero ? 'opcion--activa' : ''}`}
              onClick={() => setAvisarEnCero(!avisarEnCero)}
              aria-pressed={avisarEnCero}
            >
              Avisar al llegar a cero
              <Pastilla texto={avisarEnCero ? 'sí, aunque no tenga mínimo' : 'no avisa'} tono={avisarEnCero ? 'aviso' : 'neutro'} />
            </button>
            <p className="aviso-caja">
              {avisarEnCero
                ? 'Cuando se acabe sale en los avisos del resumen y en el correo de quien compra, una sola vez.'
                : `Sin mínimo, este ${vocabulario.insumo} no avisa nunca que se acabó. Para lo que nadie cuenta: el cable, la mica, el cargador.`}
            </p>
          </>
        ) : null}

        <button
          type="button"
          className={`opcion ${conLote ? 'opcion--activa' : ''}`}
          onClick={() => setConLote(!conLote)}
          aria-pressed={conLote}
          disabled={loteBloqueado}
        >
          Se controla por lote
          <Pastilla
            texto={conLote ? 'sí, caduca' : 'no, a granel'}
            tono={conLote ? 'aviso' : 'neutro'}
          />
        </button>
        <p className="aviso-caja">
          {loteBloqueado
            ? `Tiene ${cantidad(insumo.quantity)} ${insumo.unit} en el estante. Para cambiar si va por lote, primero cuéntalo a cero o dale salida: si no, lo que ya hay se quedaría sin lote.`
            : conLote
              ? 'Cada entrada pedirá su código de lote y su vencimiento, y las salidas saldrán por el que venza antes.'
              : 'Entra y sale sin rastro de lote. Es lo correcto para lo que no caduca: el arroz, las servilletas.'}
        </p>

        {ofreceSerie ? (
          <>
            <button
              type="button"
              className={`opcion ${porSerie ? 'opcion--activa' : ''}`}
              onClick={() => setPorSerie(!porSerie)}
              aria-pressed={porSerie}
              disabled={loteBloqueado}
            >
              Se controla por serie o IMEI
              <Pastilla
                texto={porSerie ? 'sí, cada equipo' : 'no, por cantidad'}
                tono={porSerie ? 'aviso' : 'neutro'}
              />
            </button>
            <p className="aviso-caja">
              {porSerie
                ? 'Cada unidad entra con su serie, su costo y su estado, y la caja vende ese equipo y ningún otro: es lo que encuentra la garantía. Se recibe serie por serie en Recepción.'
                : 'Para lo que tiene garantía por equipo: celulares, laptops, televisores. Con el estante vacío se puede encender.'}
            </p>
          </>
        ) : null}

        {/*
         * Lo perecible: cada lote entra con su fecha aunque el negocio no se la
         * pida a todo. El insumo de un producto marcado «Perecible» en la carta
         * ya nace así; aquí se ve y se cambia.
         */}
        {conLote ? (
          <button
            type="button"
            className={`opcion ${conFecha ? 'opcion--activa' : ''}`}
            onClick={() => setConFecha(!conFecha)}
            aria-pressed={conFecha}
          >
            Vencimiento obligatorio
            <Pastilla
              texto={conFecha ? 'sí, perecible' : 'si lo trae'}
              tono={conFecha ? 'aviso' : 'neutro'}
            />
          </button>
        ) : null}

        {editando || porSerie ? null : (
          <>
            <h3 className="formulario__subtitulo">Lo que hay hoy</h3>
            <div className="formulario__fila">
              <div className="campo">
                <Rotulo htmlFor="existencia" ayuda="Lo que hay hoy en el estante al dar de alta el insumo. Entra al historial como inventario inicial, con su costo.">Cantidad en el almacén</Rotulo>
                <input
                  id="existencia"
                  inputMode="decimal"
                  placeholder="0"
                  value={existencia}
                  onChange={(e) => setExistencia(e.target.value)}
                />
              </div>
              <div className="campo">
                <Rotulo htmlFor="costo" ayuda="Cuánto costó cada unidad, sin IGV. Es la base del valor del almacén y del costo de las recetas: sin él todo sale gratis.">Costo por {unidadElegida} (S/)</Rotulo>
                <input
                  id="costo"
                  inputMode="decimal"
                  placeholder="38.50"
                  value={costo}
                  onChange={(e) => setCosto(e.target.value)}
                  disabled={!conExistencia}
                />
              </div>
            </div>

            {conExistencia && conLote ? (
              <div className="formulario__fila">
                <div className="campo">
                  {exigeLoteDelFabricante ? (
                    <Rotulo htmlFor="lote" ayuda="El número de lote impreso en la caja. Aquí es obligatorio: es el que se busca el día que el fabricante retira un lote.">Lote del fabricante</Rotulo>
                  ) : (
                    <Rotulo htmlFor="lote" ayuda="El número de lote que trae el envase, si lo tiene: es el que te pedirá el proveedor si hay un reclamo. Si lo dejas vacío, el sistema le pone uno con la fecha.">Lote del proveedor (opcional)</Rotulo>
                  )}
                  <input
                    id="lote"
                    placeholder={exigeLoteDelFabricante ? 'El que trae la caja' : 'Se pone solo'}
                    value={lote}
                    onChange={(e) => setLote(e.target.value)}
                  />
                </div>
                <div className="campo">
                  <Rotulo htmlFor="vence" ayuda="La fecha de vencimiento del envase. El almacén saca primero lo que vence antes y avisa unos días antes de que se pase.">Vence</Rotulo>
                  <input
                    id="vence"
                    type="date"
                    value={vence}
                    onChange={(e) => setVence(e.target.value)}
                  />
                </div>
              </div>
            ) : null}

            <p className="nota-sm nota--pegada">
              {conExistencia
                ? 'Entra en el kardex como inventario inicial, con este costo: así el almacén vale lo que vale y las recetas cuestan lo que cuestan.'
                : 'Déjalo vacío si todavía no hay nada: lo que llegue después se registra en Recepción.'}
            </p>
          </>
        )}

        {guardar.isError ? (
          <>
            <Fallo error={guardar.error} />
            {conExistencia ? (
              <p className="nota-sm nota--pegada">
                Si el insumo llegó a guardarse, volver a pulsar no lo duplica ni
                mete dos veces la existencia.
              </p>
            ) : null}
          </>
        ) : null}

        <div className="formulario__fila">
          <Boton onClick={alta.cerrar}>Cancelar</Boton>
          <Boton
            variante="primario"
            onClick={() => guardar.mutate()}
            disabled={!completo || guardar.isPending}
          >
            {guardar.isPending ? 'Guardando…' : editando ? 'Guardar cambios' : 'Dar de alta'}
          </Boton>
        </div>
      </div>
    </Modal>
  );
}

/** `"50.000"` → `"50"`, `"0.250"` → `"0.25"`. Un entero sin punto se deja. */
function sinCerosDeMas(valor: string): string {
  return valor.includes('.') ? valor.replace(/\.?0+$/, '') : valor;
}

/** Los días de vida útil tecleados: vacío es «no se sabe», no cero. */
function diasDeVida(texto: string): number | null {
  if (texto.trim() === '') return null;
  const dias = Number.parseInt(texto, 10);
  if (!Number.isInteger(dias) || dias < 1 || dias > 3650) {
    throw new EntradaInvalida('Lo que dura va en días, de 1 a 3650.');
  }
  return dias;
}

// ── Receta ────────────────────────────────────────────────────────────────

/** Las mermas que se ofrecen de un toque. «Otra» deja escribirla. */
const MERMAS = [0, 5, 10];

/** Una línea de la receta mientras se escribe. */
interface LineaEnCurso {
  supplyId: string;
  escrito: string;
  unidad: UnidadDeCocina;
}

/**
 * Qué lleva una porción de un plato.
 *
 * **Arriba solo lo que lleva.** Antes enseñaba todos los insumos del almacén
 * con una casilla cada uno: las cinco cantidades de un lomo saltado se perdían
 * entre doscientos ceros, y no se veía qué llevaba el plato. Ahora se agrega
 * buscando, se escribe en la unidad de la cocina —gramos, mililitros— y el
 * costo se ve mientras se arma. Mismas reglas que el editor del POS.
 */
export function NuevaReceta({
  onGuardado = () => {},
  productoInicial = '',
  texto = 'Nueva receta',
  extra,
}: {
  onGuardado?: () => void;

  /** El plato ya elegido: al llenar la receta vacía con la que nació. */
  productoInicial?: string;
  texto?: string;

  /**
   * La receta de un extra en vez de la de un plato: lo que se cobra aparte
   * —«doble pollo»— y también sale del almacén. Sin plato que elegir.
   */
  extra?: ExtraRecipe;
}) {
  const alta = usarAlta(onGuardado);
  const clientes = useQueryClient();

  const productos = useQuery({
    queryKey: ['productos'],
    queryFn: almacen.products,
    enabled: alta.abierto,
  });
  const insumos = useQuery({
    queryKey: ['insumos'],
    queryFn: almacen.supplies,
    enabled: alta.abierto,
  });
  const recetas = useQuery({
    queryKey: ['recetas'],
    queryFn: almacen.recipes,
    enabled: alta.abierto,
  });

  const [productId, setProductId] = useState(productoInicial);
  const [merma, setMerma] = useState<number | null>(0);
  const [otraMerma, setOtraMerma] = useState('');
  const [lineas, setLineas] = useState<LineaEnCurso[]>([]);
  const [busqueda, setBusqueda] = useState('');
  const [creando, setCreando] = useState(false);

  /*
   * El almacén entero debajo del buscador, a pedido.
   *
   * Buscar es lo rápido, pero hay quien arma la receta mirando la lista. Se
   * abre con un botón y no de entrada: abierta siempre, las cinco cantidades
   * del plato volvían a perderse entre doscientas filas.
   */
  const [verTodos, setVerTodos] = useState(false);

  /*
   * Elegir un plato que ya tiene receta la trae puesta.
   *
   * Guardar reemplaza la receta entera, y el formulario abría vacío: quien
   * entraba a cambiar el gramaje de un ingrediente guardaba solo ese, y el
   * plato perdía los otros cinco sin que nada lo dijera.
   */
  const [cargadaDe, setCargadaDe] = useState('');
  // Lo que se edita: el plato, o el extra si viene uno.
  const destino = extra?.optionId ?? productId;
  useEffect(() => {
    if (destino === '' || destino === cargadaDe || recetas.data === undefined) return;

    const existente =
      extra === undefined
        ? recetas.data.find((receta) => receta.productId === productId)
        : (extra.recipe ?? undefined);
    setCargadaDe(destino);
    if (existente === undefined) return;

    if (MERMAS.includes(existente.wastePercentage)) {
      setMerma(existente.wastePercentage);
    } else {
      setMerma(null);
      setOtraMerma(String(existente.wastePercentage).replace('.', ','));
    }
    setLineas(
      existente.lines.map((linea) => {
        const milesimas = milesimasDeStock(linea.quantity);
        const unidad = unidadPreferida(linea.unit, milesimas);
        return { supplyId: linea.supplyId, unidad, escrito: enUnidad(milesimas, unidad) };
      }),
    );
  }, [destino, productId, cargadaDe, recetas.data, extra]);

  const catalogo = insumos.data ?? [];
  const insumoDe = (id: string) => catalogo.find((insumo) => insumo.id === id);
  const plato = (productos.data ?? []).find((p) => p.id === productId);
  // Un extra se compara contra lo que se cobra por él.
  const precio =
    extra !== undefined
      ? Math.round(Number.parseFloat(extra.priceDelta) * 100)
      : plato === undefined
        ? 0
        : Math.round(Number.parseFloat(plato.price) * 100);

  const mermaElegida: number | null = (() => {
    if (merma !== null) return merma;
    if (otraMerma.trim() === '') return 0;
    const valor = Number.parseFloat(otraMerma.replace(',', '.'));
    return Number.isNaN(valor) || valor < 0 || valor >= 100 ? null : valor;
  })();

  const llenas = lineas
    .map((linea) => ({
      linea,
      insumo: insumoDe(linea.supplyId),
      milesimas: aMilesimas(linea.escrito, linea.unidad),
    }))
    .filter(
      (x): x is { linea: LineaEnCurso; insumo: Supply; milesimas: number } =>
        x.insumo !== undefined && x.milesimas !== null,
    );
  const hayInvalidas = lineas.some(
    (linea) => linea.escrito.trim() !== '' && aMilesimas(linea.escrito, linea.unidad) === null,
  );
  const costo = costoDeReceta(
    llenas.map((x) => costoDeLinea(x.milesimas, costoUnitario(x.insumo.averageCost))),
    mermaElegida ?? 0,
  );
  const sinCosto = llenas.filter((x) => costoUnitario(x.insumo.averageCost) === 0).length;
  const fc = foodCost(costo, precio);
  const salen = porcionesPosibles(
    llenas.map((x) => ({
      nombre: x.insumo.name,
      necesita: x.milesimas,
      hay: milesimasDeStock(x.insumo.quantity),
    })),
  );

  const guardar = useMutation({
    mutationFn: () =>
      almacen.saveRecipe({
        recipeId: newOperationId(),
        ...(extra === undefined
          ? { productId, name: plato?.name ?? 'Receta' }
          : { modifierOptionId: extra.optionId, name: `${extra.groupName} · ${extra.optionName}` }),
        yieldQuantity: '1',
        wastePercentage: mermaElegida ?? 0,
        lines: llenas.map((x) => ({
          supplyId: x.linea.supplyId,
          quantity: textoDeStock(x.milesimas),
        })),
      }),
    onSuccess: () => {
      setLineas([]);
      setProductId(productoInicial);
      setCargadaDe('');
      void clientes.invalidateQueries({ queryKey: ['recetas'] });
      alta.guardado();
    },
  });

  const agregar = (insumo: Supply) => {
    setLineas((previas) => [
      ...previas,
      { supplyId: insumo.id, escrito: '', unidad: unidadesDeCocina(insumo.unit)[0]! },
    ]);
    setBusqueda('');
    setCreando(false);
  };
  const cambiar = (supplyId: string, cambio: Partial<LineaEnCurso>) =>
    setLineas((previas) =>
      previas.map((l) => (l.supplyId === supplyId ? { ...l, ...cambio } : l)),
    );

  const buscado = plegar(busqueda);
  const puestos = new Set(lineas.map((l) => l.supplyId));
  const libres = catalogo.filter((insumo) => !puestos.has(insumo.id));
  const encontrados =
    buscado === ''
      ? []
      : libres
          .filter(
            (insumo) =>
              plegar(insumo.name).includes(buscado) ||
              plegar(insumo.sku).includes(buscado) ||
              plegar(insumo.category).includes(buscado),
          )
          .slice(0, 6);
  const seParecen =
    buscado !== '' && encontrados.length === 0
      ? parecidos(busqueda, libres, (i) => i.name)
      : [];

  if (!alta.abierto) {
    return (
      <Boton variante="primario" onClick={alta.abrir}>
        {texto}
      </Boton>
    );
  }

  return (
    <Modal titulo={texto} onCerrar={alta.cerrar}>
      <div className="formulario">
        {extra !== undefined ? (
          <p className="nota nota--pegada">
            Lo que lleva <strong>{extra.optionName}</strong> ({extra.groupName}) cada vez que
            se pide. Se suma a lo del plato: dos lomos con este extra descuentan dos veces lo
            de aquí.
          </p>
        ) : (
        <div className="campo">
          <Rotulo htmlFor="plato" ayuda="El plato de la carta al que pertenece esta receta. Cada vez que se venda, el almacén descontará sus ingredientes solo.">Plato que se prepara aquí</Rotulo>
          {/*
           * Solo lo que se prepara. Una gaseosa entra hecha y sale hecha: se
           * vincula con «Producto de reventa», que es una línea y no una
           * receta. Un pisco sour sí está: la botella se gasta de a poco.
           */}
          <Buscable
            id="plato"
            valor={productId}
            onCambio={(elegido) => {
              setProductId(elegido);
              setLineas([]);
            }}
            placeholder="Busca el plato…"
            opciones={(productos.data ?? [])
              .filter((p) => !esReventa(p))
              .map((p) => ({
                valor: p.id,
                etiqueta: p.name,
                detalle: p.hasRecipe ? 'Ya tiene receta' : undefined,
              }))}
          />
        </div>
        )}

        <p className="versal">Lo que lleva una porción</p>
        {lineas.length === 0 ? (
          <p className="nota nota--pegada">
            Todavía no lleva nada. Busca abajo el primer ingrediente.
          </p>
        ) : (
          <ul className="lista">
            {lineas.map((linea) => {
              const insumo = insumoDe(linea.supplyId);
              if (insumo === undefined) return null;
              const milesimas = aMilesimas(linea.escrito, linea.unidad);
              const unidades = unidadesDeCocina(insumo.unit);
              const tieneCosto = costoUnitario(insumo.averageCost) > 0;
              const aviso =
                milesimas === null ? undefined : avisoDeLinea(insumo.unit, linea.unidad, milesimas);

              return (
                <li key={linea.supplyId} className="receta-linea">
                  <div className="lista__fila">
                    <div className="lista__texto">
                      <strong>{insumo.name}</strong>
                      <span className="apagado">
                        {tieneCosto
                          ? `quedan ${cantidad(insumo.quantity)} ${insumo.unit}`
                          : 'sin costo todavía: nunca entró al almacén'}
                      </span>
                    </div>
                    <input
                      className="celda-input"
                      inputMode="decimal"
                      aria-label={`Cuánto lleva de ${insumo.name}`}
                      placeholder="0"
                      value={linea.escrito}
                      onChange={(e) => cambiar(linea.supplyId, { escrito: e.target.value })}
                    />
                    <div className="filtros filtros--pegados">
                      {unidades.length === 1 ? (
                        <span className="mono apagado">{insumo.unit}</span>
                      ) : (
                        unidades.map((unidad) => (
                          <Filtro
                            key={unidad.codigo}
                            texto={unidad.codigo}
                            activo={linea.unidad.codigo === unidad.codigo}
                            onClick={() => cambiar(linea.supplyId, { unidad })}
                          />
                        ))
                      )}
                    </div>
                    <span className="mono receta-linea__costo">
                      {milesimas === null
                        ? '—'
                        : tieneCosto
                          ? soles(costoDeLinea(milesimas, costoUnitario(insumo.averageCost)) / 100)
                          : 'sin costo'}
                    </span>
                    <Boton
                      onClick={() =>
                        setLineas((previas) => previas.filter((l) => l.supplyId !== linea.supplyId))
                      }
                    >
                      Quitar
                    </Boton>
                  </div>
                  {milesimas !== null && linea.unidad.codigo !== insumo.unit ? (
                    <p className="nota-sm receta-linea__nota">
                      = {textoDeStock(milesimas).replace('.', ',')} {insumo.unit}
                    </p>
                  ) : linea.escrito.trim() !== '' && milesimas === null ? (
                    <p className="nota-sm receta-linea__nota">
                      {linea.unidad.milesimasPorUnidad === 1
                        ? `En ${linea.unidad.codigo} va sin decimales`
                        : 'No se entiende la cantidad'}
                    </p>
                  ) : null}
                  {aviso === undefined ? null : (
                    <div className="aviso-caja aviso-caja--aviso">
                      ¿{linea.escrito} {linea.unidad.codigo} de {insumo.name} en una sola porción?{' '}
                      {aviso === null ? (
                        'Revísalo: se descuenta eso cada vez que se venda.'
                      ) : (
                        <>
                          Quizá quisiste {linea.escrito} {aviso.codigo}.{' '}
                          <Boton onClick={() => cambiar(linea.supplyId, { unidad: aviso })}>
                            Son {linea.escrito} {aviso.codigo}
                          </Boton>
                        </>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}

        {creando ? (
          <InsumoAlVuelo
            nombreInicial={busqueda}
            onCancelar={() => setCreando(false)}
            onCreado={async (supplyId) => {
              const nuevos = await clientes.fetchQuery({
                queryKey: ['insumos'],
                queryFn: almacen.supplies,
                staleTime: 0,
              });
              const nuevo = nuevos.find((i) => i.id === supplyId);
              if (nuevo !== undefined) agregar(nuevo);
            }}
          />
        ) : (
          <div className="buscador-de-lista receta-buscador">
            <input
              className="buscador"
              type="search"
              aria-label="Agregar ingrediente"
              placeholder="Agregar ingrediente: busca por nombre, código o categoría…"
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
            />
          </div>
        )}

        {!creando && buscado === '' && libres.length > 0 ? (
          <div>
            <Boton onClick={() => setVerTodos((antes) => !antes)}>
              {verTodos ? 'Ocultar la lista' : `Ver todos los insumos (${libres.length})`}
            </Boton>
          </div>
        ) : null}

        {!creando && buscado === '' && verTodos ? (
          <ul className="lista">
            {[...libres]
              .sort((a, b) => a.category.localeCompare(b.category) || a.name.localeCompare(b.name))
              .map((insumo, indice, ordenados) => (
                <li key={insumo.id}>
                  {indice === 0 || ordenados[indice - 1]!.category !== insumo.category ? (
                    <p className="versal receta-categoria">{insumo.category}</p>
                  ) : null}
                  <button
                    type="button"
                    className="lista__fila receta-resultado"
                    onClick={() => agregar(insumo)}
                  >
                    <div className="lista__texto">
                      <strong>+ {insumo.name}</strong>
                      <span className="apagado">
                        se cuenta en {insumo.unit} · quedan {cantidad(insumo.quantity)}
                      </span>
                    </div>
                  </button>
                </li>
              ))}
          </ul>
        ) : null}

        {!creando && encontrados.length > 0 ? (
          <ul className="lista">
            {encontrados.map((insumo) => (
              <li key={insumo.id}>
                    <button type="button" className="lista__fila receta-resultado" onClick={() => agregar(insumo)}>                <div className="lista__texto">
                  <strong>+ {insumo.name}</strong>
                  <span className="apagado">
                    {insumo.category} · se cuenta en {insumo.unit}
                  </span>
                </div>
              </button>
                  </li>
            ))}
          </ul>
        ) : null}

        {!creando && buscado !== '' && encontrados.length === 0 ? (
          <>
            <p className="nota nota--pegada">No hay ningún insumo «{busqueda.trim()}».</p>
            {/*
             * Antes de crear otro, lo que se parece: «Culantro» y «Cilantro
             * fresco» contando cada uno la mitad es lo que descuadra un conteo.
             */}
            {seParecen.length > 0 ? (
              <ul className="lista">
                {seParecen.map((insumo) => (
                  <li key={insumo.id}>
                    <button type="button" className="lista__fila receta-resultado" onClick={() => agregar(insumo)}>                    <div className="lista__texto">
                      <strong>¿Querías decir «{insumo.name}»?</strong>
                      <span className="apagado">
                        {insumo.category} · se cuenta en {insumo.unit}
                      </span>
                    </div>
                  </button>
                  </li>
                ))}
              </ul>
            ) : null}
            <div>
              <Boton onClick={() => setCreando(true)}>
                + Crear «{mayuscula(busqueda.trim())}» como insumo nuevo
              </Boton>
            </div>
          </>
        ) : null}

        <div className="receta-merma">
          <span className="rotulo rotulo--suelto">¿Se pierde algo al prepararlo?</span>
          <p className="nota-sm">
            Recortes, lo que queda en la olla. Se pagó igual que lo que llega a la mesa, así que
            va dentro del costo del plato.
          </p>
          <div className="filtros">
            {MERMAS.map((valor) => (
              <Filtro
                key={valor}
                texto={valor === 0 ? 'Nada' : `${valor} %`}
                activo={merma === valor}
                onClick={() => setMerma(valor)}
              />
            ))}
            <Filtro texto="Otra" activo={merma === null} onClick={() => setMerma(null)} />
            {merma === null ? (
              <input
                className="celda-input"
                inputMode="decimal"
                aria-label="Merma en porcentaje"
                placeholder="%"
                value={otraMerma}
                onChange={(e) => setOtraMerma(e.target.value)}
              />
            ) : null}
          </div>
          {mermaElegida === null ? (
            <p className="aviso-caja aviso-caja--aviso">La merma va de 0 a 99 %.</p>
          ) : null}
        </div>

        {llenas.length > 0 ? (
          <div className="sugerido receta-resumen">
            <div>
              <strong className="mono">
                {precio > 0
                  ? `Cuesta ${soles(costo / 100)} · el ${porcentaje(fc, 0)} de ${soles(precio / 100)}`
                  : `Cuesta ${soles(costo / 100)} hacerlo`}
              </strong>
              {salen.limita === null ? null : (
                <p className="nota-sm">
                  {salen.porciones === 0
                    ? `Con lo que hay hoy no sale ninguno: falta ${salen.limita}.`
                    : `Con lo que hay en el almacén salen ${salen.porciones} · se acaba primero: ${salen.limita}.`}
                </p>
              )}
            </div>
            {precio > 0 && sinCosto === 0 ? (
              <Pastilla
                texto={fc <= 30 ? 'En meta' : fc <= 33 ? 'Al límite' : 'Sobre meta'}
                tono={fc <= 30 ? 'exito' : fc <= 33 ? 'aviso' : 'peligro'}
              />
            ) : null}
          </div>
        ) : null}

        {sinCosto > 0 ? (
          <p className="aviso-caja aviso-caja--aviso">
            {sinCosto === 1
              ? 'Un ingrediente todavía no tiene costo'
              : `${sinCosto} ingredientes todavía no tienen costo`}
            : el plato cuesta más de lo que dice. Se arregla recibiéndolo una vez en el almacén.
          </p>
        ) : null}
        {precio > 0 && costo > precio ? (
          <p className="aviso-caja aviso-caja--peligro">
            Los insumos cuestan más que el plato ({soles(costo / 100)} contra {soles(precio / 100)}).
            Revisa las cantidades: casi siempre es un gramo escrito en kilos.
          </p>
        ) : null}

        {guardar.isError ? <Fallo error={guardar.error} /> : null}

        <div className="formulario__fila">
          <Boton onClick={alta.cerrar}>Cancelar</Boton>
          <Boton
            variante="primario"
            onClick={() => guardar.mutate()}
            disabled={
              destino === '' ||
              llenas.length === 0 ||
              hayInvalidas ||
              mermaElegida === null ||
              creando ||
              guardar.isPending
            }
          >
            {guardar.isPending
              ? 'Guardando…'
              : llenas.length === 0
                ? 'Agrega al menos un ingrediente'
                : `Guardar con ${contados(llenas.length, 'ingrediente', 'ingredientes')}`}
          </Boton>
        </div>
      </div>
    </Modal>
  );
}

/**
 * Dar de alta un insumo sin salir de la receta.
 *
 * Lo mínimo para contarlo: nombre, categoría y en qué se cuenta. El código lo
 * pone el almacén con la serie de la categoría; el mínimo, el proveedor y el
 * costo se completan en su ficha. Pedirlos aquí es sacar al cocinero de la
 * receta que estaba escribiendo. Sin el permiso del almacén, el servidor lo
 * rechaza y se dice con su mensaje.
 */
function InsumoAlVuelo({
  nombreInicial,
  onCancelar,
  onCreado,
}: {
  nombreInicial: string;
  onCancelar: () => void;
  onCreado: (supplyId: string) => Promise<void>;
}) {
  const opciones = useQuery({ queryKey: ['opciones'], queryFn: almacen.options });
  const [nombre, setNombre] = useState(mayuscula(nombreInicial.trim()));
  const [categoria, setCategoria] = useState('');
  const [unidad, setUnidad] = useState('');
  const [supplyId] = useState(newOperationId);

  const crear = useMutation({
    mutationFn: async () => {
      await almacen.saveSupply({
        supplyId,
        name: nombre.trim(),
        categoryId: categoria,
        stockUnitId: unidad,
        isLotTracked: false,
      });
      await onCreado(supplyId);
    },
  });

  return (
    <div className="tarjeta">
      <p className="versal">Insumo nuevo</p>
      <p className="nota-sm">
        Lo mínimo para contarlo. El código lo pone el almacén; el costo, el mínimo y el proveedor
        se completan en su ficha.
      </p>
      <div className="campo">
        <Rotulo htmlFor="insumo-nuevo" ayuda="Como lo llaman en la cocina y en la guía del proveedor.">Nombre</Rotulo>
        <input id="insumo-nuevo" value={nombre} onChange={(e) => setNombre(e.target.value)} />
      </div>
      {opciones.isError ? <Fallo error={opciones.error} /> : null}
      {opciones.data === undefined ? null : (
        <>
          <span className="rotulo rotulo--suelto">En qué se cuenta</span>
          <div className="filtros">
            {opciones.data.units.map((u) => (
              <Filtro key={u.id} texto={u.code} activo={unidad === u.id} onClick={() => setUnidad(u.id)} />
            ))}
          </div>
          <span className="rotulo rotulo--suelto">Categoría</span>
          <div className="filtros">
            {opciones.data.categories.map((c) => (
              <Filtro
                key={c.id}
                texto={c.name}
                activo={categoria === c.id}
                onClick={() => setCategoria(c.id)}
              />
            ))}
          </div>
        </>
      )}
      {crear.isError ? <Fallo error={crear.error} /> : null}
      <div className="formulario__fila">
        <Boton onClick={onCancelar} disabled={crear.isPending}>
          Volver a buscar
        </Boton>
        <Boton
          variante="primario"
          onClick={() => crear.mutate()}
          disabled={crear.isPending || nombre.trim() === '' || categoria === '' || unidad === ''}
        >
          {crear.isPending ? 'Creando…' : 'Crear y agregar'}
        </Boton>
      </div>
    </div>
  );
}

// ── Producto de reventa ───────────────────────────────────────────────────

/**
 * Lo que se compra hecho y se vende tal cual: una gaseosa, un paquete de
 * galletas, una botella cerrada.
 *
 * Por dentro es una receta de una sola línea y sin merma —no hace falta otra
 * tabla para decir «una venta gasta una unidad»— pero por fuera no se llama
 * receta ni se pide como tal. Quien tiene cuarenta bebidas en la carta no va a
 * abrir cuarenta escandallos, y si no las vincula, cada lata que sale del
 * refrigerador se queda en el sistema para siempre.
 *
 * La cantidad no siempre es uno: hay locales que compran la gaseosa por caja
 * de 12 y la venden por lata. Ahí lo que sale del almacén por venta es 1/12 de
 * caja, y eso lo sabe el local, no el sistema.
 */
export function VincularReventa({
  onGuardado = () => {},
  productoInicial = '',
}: {
  onGuardado?: () => void;
  productoInicial?: string;
}) {
  const alta = usarAlta(onGuardado);

  const productos = useQuery({
    queryKey: ['productos'],
    queryFn: almacen.products,
    enabled: alta.abierto,
  });
  const insumos = useQuery({
    queryKey: ['insumos'],
    queryFn: almacen.supplies,
    enabled: alta.abierto,
  });

  const [productId, setProductId] = useState(productoInicial);
  const [supplyId, setSupplyId] = useState('');
  const [porVenta, setPorVenta] = useState('1');

  const insumo = (insumos.data ?? []).find((i) => i.id === supplyId);

  const guardar = useMutation({
    mutationFn: () => {
      const producto = (productos.data ?? []).find((p) => p.id === productId);

      return almacen.saveRecipe({
        recipeId: newOperationId(),
        productId,
        name: producto?.name ?? 'Reventa',
        yieldQuantity: '1',
        // Cero merma: lo que entra cerrado sale cerrado. Poner un porcentaje
        // aquí inventaría un costo que nadie tuvo.
        wastePercentage: 0,
        lines: [{ supplyId, quantity: porVenta }],
      });
    },
    onSuccess: () => {
      setProductId('');
      setSupplyId('');
      setPorVenta('1');
      alta.guardado();
    },
  });

  if (!alta.abierto) {
    return <Boton onClick={alta.abrir}>Vincular reventa</Boton>;
  }

  const sinPreparar = (productos.data ?? []).filter(
    (producto) => esReventa(producto),
  );

  return (
    <Modal titulo="Producto de reventa" onCerrar={alta.cerrar}>
      <div className="formulario">
        <p className="aviso-caja">
          Esto es para lo que entra hecho y sale hecho. No lleva escandallo: se
          dice de qué insumo sale y cuánto gasta cada venta, y a partir de ahí
          el stock baja solo. Lo que se prepara aquí —incluidos los cocteles,
          que gastan la botella de a poco— va por «Nueva receta».
        </p>

        <div className="campo">
          <Rotulo htmlFor="reventa-producto" ayuda="Lo que se compra hecho y se vende tal cual: una gaseosa, una botella cerrada.">Producto de la carta</Rotulo>
          <Buscable
            id="reventa-producto"
            valor={productId}
            onCambio={setProductId}
            placeholder="Busca el producto…"
            opciones={sinPreparar.map((producto) => ({
              valor: producto.id,
              etiqueta: producto.name,
              detalle: producto.hasRecipe ? 'Ya vinculado' : undefined,
            }))}
          />
        </div>

        {sinPreparar.length === 0 ? (
          <p className="nota nota--pegada">
            Nada de la carta está marcado como que se compra hecho. En la
            Carta del POS, edita la gaseosa y marca «Se compra hecho»: puede
            seguir saliendo por la Barra.
          </p>
        ) : null}

        <div className="campo">
          <Rotulo htmlFor="reventa-insumo" ayuda="El insumo del almacén que se gasta con cada venta de este producto.">De qué insumo sale</Rotulo>
          <Buscable
            id="reventa-insumo"
            valor={supplyId}
            onCambio={setSupplyId}
            placeholder="Busca el insumo por nombre o código…"
            opciones={(insumos.data ?? []).map((disponible) =>
              opcionDeInsumo(disponible, `se cuenta en ${disponible.unit}`),
            )}
          />
        </div>

        <div className="campo">
          <Rotulo htmlFor="reventa-cantidad" ayuda="Cuánto sale del almacén por cada venta. Casi siempre 1; si se compra por caja de 12 y se vende por unidad, 0,083.">
            Cuánto gasta cada venta{insumo === undefined ? '' : ` (${insumo.unit})`}
          </Rotulo>
          <input
            id="reventa-cantidad"
            inputMode="decimal"
            value={porVenta}
            onChange={(e) => setPorVenta(e.target.value)}
          />
          <p className="nota-sm nota--pegada">
            Normalmente 1. Es distinto si se compra por caja y se vende por
            unidad: una caja de 12 gasta 0,083 por lata.
          </p>
        </div>

        {guardar.isError ? <Fallo error={guardar.error} /> : null}

        <div className="formulario__fila">
          <Boton onClick={alta.cerrar}>Cancelar</Boton>
          <Boton
            variante="primario"
            onClick={() => guardar.mutate()}
            disabled={
              productId === '' ||
              supplyId === '' ||
              porVenta.trim() === '' ||
              guardar.isPending
            }
          >
            {guardar.isPending ? 'Guardando…' : 'Vincular'}
          </Boton>
        </div>
      </div>
    </Modal>
  );
}

/** Un nombre para comparar: sin mayúsculas ni espacios de sobra. */
function nombreComparable(nombre: string): string {
  return nombre.trim().toLowerCase();
}

/**
 * Vincular toda la reventa de una vez.
 *
 * El formulario de arriba es uno por producto, y antes había que dar de alta
 * el insumo en otra pantalla: con treinta bebidas y snacks era una tarde, así
 * que la lista no se empezaba y cada lata vendida se quedaba en el sistema.
 * Aquí sale todo lo que se compra hecho y todavía no descuenta, ya marcado:
 * cada uno con el insumo que ya se llame igual o con uno nuevo con su nombre,
 * a una unidad por venta. La caja de doce que se vende por lata se corrige
 * después en el formulario de uno.
 *
 * Parte de la carta y no de lo ya vendido: la gaseosa que se cargó ayer no ha
 * salido todavía en «Reventa sin vincular», y es justo la que conviene dejar
 * lista antes de su primera venta.
 */
export function VincularReventaEnBloque({
  onGuardado = () => {},
}: {
  onGuardado?: () => void;
}) {
  const alta = usarAlta(onGuardado);

  const productos = useQuery({
    queryKey: ['productos'],
    queryFn: almacen.products,
    enabled: alta.abierto,
  });
  const insumos = useQuery({
    queryKey: ['insumos'],
    queryFn: almacen.supplies,
    enabled: alta.abierto,
  });

  const sinVincular = (productos.data ?? []).filter(
    (producto) => esReventa(producto) && !producto.hasRecipe,
  );

  /** Lo que se dejó fuera a mano. Todo lo demás va. */
  const [fuera, setFuera] = useState<Set<string>>(new Set());
  /** El insumo elegido a mano por producto. Vacío: el que se llame igual, o uno nuevo. */
  const [elegido, setElegido] = useState<Record<string, string>>({});
  /**
   * Los identificadores de cada intento, fijos mientras el formulario esté
   * abierto: si se corta a medias, volver a pulsar termina lo que faltó en vez
   * de crear otro insumo.
   */
  const [ids] = useState(() => new Map<string, { recipeId: string; newSupplyId: string }>());
  const idsDe = (productId: string) => {
    let par = ids.get(productId);
    if (par === undefined) {
      par = { recipeId: newOperationId(), newSupplyId: newOperationId() };
      ids.set(productId, par);
    }
    return par;
  };

  const porNombre = new Map(
    (insumos.data ?? []).map((insumo) => [nombreComparable(insumo.name), insumo]),
  );

  const marcados = sinVincular.filter((producto) => !fuera.has(producto.id));

  const guardar = useMutation({
    mutationFn: () =>
      almacen.linkResale({
        links: marcados.map((producto) => ({
          productId: producto.id,
          ...idsDe(producto.id),
          existingSupplyId: elegido[producto.id] || null,
        })),
      }),
    onSuccess: () => {
      setFuera(new Set());
      setElegido({});
      ids.clear();
      alta.guardado();
    },
  });

  if (!alta.abierto) {
    return <Boton onClick={alta.abrir}>Vincular toda la reventa</Boton>;
  }

  const cargando = productos.isLoading || insumos.isLoading;

  return (
    <Modal titulo="Vincular toda la reventa" onCerrar={alta.cerrar}>
      <div className="formulario">
        <p className="aviso-caja">
          Lo que se compra hecho y se vende tal cual —gaseosas, aguas,
          galletas— y todavía no descuenta del almacén. Cada uno sale con su
          insumo y una unidad por venta: el que ya se llame igual, o uno nuevo
          con su nombre en la categoría «Reventa». Desde ahí, cada venta baja
          el stock sola.
        </p>

        {cargando ? (
          <p className="nota">Cargando la carta…</p>
        ) : sinVincular.length === 0 ? (
          // Dos vacíos distintos que antes decían lo mismo: «ya descuenta
          // todo» cuando en la carta no había nada marcado como reventa
          // mandaba a buscar el fallo en el sitio equivocado.
          (productos.data ?? []).some(esReventa) ? (
            <p className="nota">
              Toda la reventa de la carta ya descuenta del almacén. Lo que se
              prepara aquí —incluidos los cocteles— va por «Nueva receta».
            </p>
          ) : (
            <p className="nota">
              Nada de la carta está marcado como que se compra hecho, así que
              no hay reventa que vincular. En la Carta del POS, edita la
              gaseosa o el agua y marca «Se compra hecho» —puede seguir
              saliendo por la Barra—; después aparece aquí.
            </p>
          )
        ) : (
          <ul className="lista">
            {sinVincular.map((producto) => {
              const va = !fuera.has(producto.id);
              const mismoNombre = porNombre.get(nombreComparable(producto.name));

              return (
                <li key={producto.id} className="lista__fila">
                  <label className="lista__texto">
                    <span>
                      <input
                        type="checkbox"
                        checked={va}
                        onChange={() =>
                          setFuera((antes) => {
                            const despues = new Set(antes);
                            if (va) despues.add(producto.id);
                            else despues.delete(producto.id);
                            return despues;
                          })
                        }
                      />{' '}
                      <strong>{producto.name}</strong>
                    </span>
                  </label>
                  <Buscable
                    etiquetaAccesible={`Insumo de ${producto.name}`}
                    valor={elegido[producto.id] ?? ''}
                    deshabilitado={!va}
                    onCambio={(insumoId) =>
                      setElegido((antes) => ({ ...antes, [producto.id]: insumoId }))
                    }
                    sinEleccion={
                      mismoNombre === undefined
                        ? 'Insumo nuevo con su nombre'
                        : `El que ya se llama así (${mismoNombre.sku})`
                    }
                    opciones={(insumos.data ?? []).map((insumo) =>
                      opcionDeInsumo(insumo, `se cuenta en ${insumo.unit}`),
                    )}
                  />
                </li>
              );
            })}
          </ul>
        )}

        {guardar.isError ? <Fallo error={guardar.error} /> : null}

        <div className="formulario__fila">
          <Boton onClick={alta.cerrar}>Cancelar</Boton>
          <Boton
            variante="primario"
            onClick={() => guardar.mutate()}
            disabled={marcados.length === 0 || guardar.isPending}
          >
            {guardar.isPending
              ? 'Vinculando…'
              : `Vincular ${contados(marcados.length, 'producto', 'productos')}`}
          </Boton>
        </div>
      </div>
    </Modal>
  );
}

// ── Devolución y traspaso ─────────────────────────────────────────────────

/**
 * Devolver al proveedor lo que llegó mal.
 *
 * Existe aparte de la merma justamente para no ser una merma. Lo que se
 * devuelve no se perdió: se cambia o se descuenta de la próxima factura. Cuando
 * esto se registraba como merma —que era la única forma— la cifra del mes
 * culpaba a la cocina de lo que había hecho el proveedor, y la lista de motivos
 * de merma, que existe para saber si el problema es la cámara, el proveedor o
 * el personal, dejaba de servir para eso.
 */
export function Devolucion({ onGuardado = () => {} }: { onGuardado?: () => void }) {
  const alta = usarAlta(onGuardado);
  const vocabulario = useVocabulario();

  const insumos = useQuery({
    queryKey: ['insumos'],
    queryFn: almacen.supplies,
    enabled: alta.abierto,
  });

  const [supplyId, setSupplyId] = useState('');
  const [cuanto, setCuanto] = useState('');
  const [papel, setPapel] = useState('');

  const elegido = (insumos.data ?? []).find((i) => i.id === supplyId);

  const lotes = useQuery({
    queryKey: ['lotes', supplyId],
    queryFn: () => almacen.lots(supplyId),
    enabled: alta.abierto && supplyId !== '' && (elegido?.isLotTracked ?? false),
  });

  const [lotId, setLotId] = useState('');
  const [operationId, renovarOperacion] = usarOperacion();

  const guardar = useMutation({
    mutationFn: () =>
      almacen.returnToSupplier({
        operationId,
        supplyId,
        quantity: cuanto,
        lotId: lotId === '' ? null : lotId,
        document: papel.trim() === '' ? null : papel.trim(),
      }),
    onSuccess: () => {
      setSupplyId('');
      setCuanto('');
      setPapel('');
      setLotId('');
      renovarOperacion();
      alta.guardado();
    },
  });

  if (!alta.abierto) {
    return <Boton onClick={alta.abrir}>Devolver al proveedor</Boton>;
  }

  return (
    <Modal titulo="Devolución al proveedor" onCerrar={alta.cerrar}>
      <div className="formulario">
        <p className="aviso-caja">
          Esto no es una merma. Sale del almacén igual, pero queda como
          devolución: lo que llegó picado o de menos no lo perdió el local, y la
          merma del mes tiene que poder decir de quién fue cada pérdida.
        </p>

        <div className="campo">
          <Rotulo htmlFor="dev-insumo" ayuda="Lo que se le devuelve al proveedor porque llegó mal o de más. Sale del almacén, pero no cuenta como pérdida del local.">{mayuscula(vocabulario.insumo)}</Rotulo>
          <Buscable
            id="dev-insumo"
            valor={supplyId}
            onCambio={(insumoId) => {
              setSupplyId(insumoId);
              setLotId('');
            }}
            placeholder={`Busca el ${vocabulario.insumo} o el proveedor…`}
            opciones={(insumos.data ?? []).map((insumo) =>
              opcionDeInsumo(insumo, insumo.supplier ?? 'sin proveedor fijo'),
            )}
          />
        </div>

        {elegido?.isLotTracked ? (
          <div className="campo">
            <Rotulo htmlFor="dev-lote" ayuda="El lote exacto que se devuelve. Si no eliges, sale el que vence antes.">Qué lote se devuelve</Rotulo>
            <select
              id="dev-lote"
              value={lotId}
              onChange={(e) => setLotId(e.target.value)}
            >
              <option value="">El que vence antes</option>
              {(lotes.data ?? []).map((lote) => (
                <option key={lote.id} value={lote.id}>
                  {lote.code} · quedan {cantidad(lote.quantity)}
                  {lote.expiresAt === null
                    ? ' · sin fecha'
                    : ` · vence el ${fechaDeCalendario(lote.expiresAt)} (${cuantoFalta(lote.expiresAt, diaDeLima())})`}
                </option>
              ))}
            </select>
            <p className="nota-sm nota--pegada">
              Quien tiene la caja delante sabe cuál es mejor que cualquier regla.
              Sin elegir, sale el que vence antes.
            </p>
          </div>
        ) : null}

        <div className="formulario__fila">
          <div className="campo">
            <Rotulo htmlFor="dev-cuanto" ayuda="La cantidad que se devuelve, en la unidad en que se cuenta el insumo.">
              Cuánto{elegido === undefined ? '' : ` (${elegido.unit})`}
            </Rotulo>
            <input
              id="dev-cuanto"
              inputMode="decimal"
              placeholder="0"
              value={cuanto}
              onChange={(e) => setCuanto(e.target.value)}
            />
          </div>
          <div className="campo">
            <Rotulo htmlFor="dev-papel" ayuda="La guía o nota con la que se devuelve, para poder reclamar el descuento al proveedor.">Documento (opcional)</Rotulo>
            <input
              id="dev-papel"
              placeholder="Guía de devolución 0012"
              value={papel}
              onChange={(e) => setPapel(e.target.value)}
            />
          </div>
        </div>

        {guardar.isError ? <Fallo error={guardar.error} /> : null}

        <div className="formulario__fila">
          <Boton onClick={alta.cerrar}>Cancelar</Boton>
          <Boton
            variante="primario"
            onClick={() => guardar.mutate()}
            disabled={supplyId === '' || cuanto.trim() === '' || guardar.isPending}
          >
            {guardar.isPending ? 'Guardando…' : 'Registrar devolución'}
          </Boton>
        </div>
      </div>
    </Modal>
  );
}

/**
 * Mandar mercadería a otro local del mismo restaurante.
 *
 * Sale de aquí y entra allá en la misma operación. Hacerlo a mano —una baja
 * aquí y una entrada allá— dejaba dos agujeros: la baja se contaba como merma
 * de un local que no perdió nada, y la entrada del otro se valoraba al precio
 * que alguien tecleara. Aquí **el costo viaja con la mercadería**.
 */
export function Traspaso({ onGuardado = () => {} }: { onGuardado?: () => void }) {
  const alta = usarAlta(onGuardado);
  const vocabulario = useVocabulario();

  const insumos = useQuery({
    queryKey: ['insumos'],
    queryFn: almacen.supplies,
    enabled: alta.abierto,
  });
  const opciones = useQuery({
    queryKey: ['opciones'],
    queryFn: almacen.options,
    enabled: alta.abierto,
  });

  const [supplyId, setSupplyId] = useState('');
  const [destino, setDestino] = useState('');
  const [cuanto, setCuanto] = useState('');
  const [lotId, setLotId] = useState('');

  const elegido = (insumos.data ?? []).find((i) => i.id === supplyId);
  const conLote = elegido?.isLotTracked ?? false;

  const lotes = useQuery({
    queryKey: ['lotes', supplyId],
    queryFn: () => almacen.lots(supplyId),
    enabled: alta.abierto && supplyId !== '' && conLote,
  });

  const [operationId, renovarOperacion] = usarOperacion();

  const guardar = useMutation({
    mutationFn: () =>
      almacen.transfer({
        operationId,
        supplyId,
        toBranchId: destino,
        quantity: cuanto,
        lotId: lotId === '' ? null : lotId,
      }),
    onSuccess: () => {
      setSupplyId('');
      setCuanto('');
      setLotId('');
      renovarOperacion();
      alta.guardado();
    },
  });

  if (!alta.abierto) {
    return <Boton onClick={alta.abrir}>Traspasar a otro local</Boton>;
  }

  const locales = opciones.data?.otherBranches ?? [];

  return (
    <Modal titulo="Traspaso a otro local" onCerrar={alta.cerrar}>
      <div className="formulario">
        {locales.length === 0 ? (
          <p className="aviso-caja">
            Este restaurante tiene un solo local, así que no hay a dónde
            traspasar. Aparecerá aquí en cuanto haya otro.
          </p>
        ) : null}

        <div className="campo">
          <Rotulo htmlFor="tra-insumo" ayuda={`Lo que se manda a otro local del mismo ${vocabulario.negocio}. Sale de aquí y entra allá en el mismo momento, al mismo costo.`}>{mayuscula(vocabulario.insumo)}</Rotulo>
          <Buscable
            id="tra-insumo"
            valor={supplyId}
            onCambio={(insumoId) => {
              setSupplyId(insumoId);
              setLotId('');
            }}
            placeholder={`Busca el ${vocabulario.insumo} por nombre o código…`}
            opciones={(insumos.data ?? []).map((insumo) =>
              opcionDeInsumo(insumo, `quedan ${cantidad(insumo.quantity)} ${insumo.unit}`),
            )}
          />
        </div>

        {conLote ? (
          <div className="campo">
            <Rotulo htmlFor="tra-lote" ayuda="El lote que se manda. Llega al otro local con el mismo código y la misma fecha de vencimiento.">Qué lote se manda</Rotulo>
            <select
              id="tra-lote"
              value={lotId}
              onChange={(e) => setLotId(e.target.value)}
            >
              <option value="">Elegir…</option>
              {(lotes.data ?? []).map((lote) => (
                <option key={lote.id} value={lote.id}>
                  {lote.code} · quedan {cantidad(lote.quantity)}
                  {lote.expiresAt === null
                    ? ' · sin fecha'
                    : ` · vence el ${fechaDeCalendario(lote.expiresAt)} (${cuantoFalta(lote.expiresAt, diaDeLima())})`}
                </option>
              ))}
            </select>
            <p className="nota-sm nota--pegada">
              Obligatorio en lo que se controla por lote: el lote entra en el
              otro local con su mismo código y su misma fecha. Sin decir cuál,
              el vencimiento se perdería al cruzar la puerta.
            </p>
          </div>
        ) : null}

        <div className="formulario__fila">
          <div className="campo">
            <Rotulo htmlFor="tra-destino" ayuda="El local que recibe la mercadería.">A qué local</Rotulo>
            <select
              id="tra-destino"
              value={destino}
              onChange={(e) => setDestino(e.target.value)}
            >
              <option value="">Elegir…</option>
              {locales.map((local) => (
                <option key={local.id} value={local.id}>
                  {local.name}
                </option>
              ))}
            </select>
          </div>
          <div className="campo">
            <Rotulo htmlFor="tra-cuanto" ayuda="La cantidad que se manda, en la unidad en que se cuenta el insumo.">
              Cuánto{elegido === undefined ? '' : ` (${elegido.unit})`}
            </Rotulo>
            <input
              id="tra-cuanto"
              inputMode="decimal"
              placeholder="0"
              value={cuanto}
              onChange={(e) => setCuanto(e.target.value)}
            />
          </div>
        </div>

        <p className="nota-sm nota--pegada">
          Entra en el otro local al costo que tiene aquí. Si se valorara de otra
          forma, mover un saco de sitio le cambiaría el valor al inventario del
          grupo sin que nadie comprara ni vendiera nada.
        </p>

        {guardar.isError ? <Fallo error={guardar.error} /> : null}

        <div className="formulario__fila">
          <Boton onClick={alta.cerrar}>Cancelar</Boton>
          <Boton
            variante="primario"
            onClick={() => guardar.mutate()}
            disabled={
              supplyId === '' ||
              destino === '' ||
              cuanto.trim() === '' ||
              (conLote && lotId === '') ||
              guardar.isPending
            }
          >
            {guardar.isPending ? 'Guardando…' : 'Traspasar'}
          </Boton>
        </div>
      </div>
    </Modal>
  );
}

// ── Mandar una orden de compra ────────────────────────────────────────────

/**
 * El texto de la orden, listo para que salga del sistema.
 *
 * Antes «Enviar» solo cambiaba el estado a *enviada*. Pero ¿enviada cómo? No
 * había PDF, ni texto, ni nada: era algo que el usuario le decía al sistema, y
 * el proveedor se enteraba por una llamada aparte que el almacén nunca veía.
 *
 * En Perú esto va por WhatsApp. Así que el camino corto es el de verdad: el
 * texto armado, el botón que abre el chat del proveedor con el mensaje puesto,
 * y —solo después de que la persona confirme que salió— el estado cambia. El
 * sistema no dice que se mandó algo hasta que alguien lo mandó.
 *
 * También se puede copiar o imprimir: no todos los proveedores usan WhatsApp, y
 * al distribuidor grande se le manda por correo o se le entrega en papel.
 */
export function MandarOrden({
  orden,
  onEnviada,
}: {
  orden: PurchaseOrder;
  onEnviada: () => void;
}) {
  const alta = usarAlta(() => {});
  const [copiado, setCopiado] = useState(false);

  const marcar = useMutation({
    mutationFn: () => almacen.changeOrderStatus(orden.id, 'sent'),
    onSuccess: () => {
      alta.cerrar();
      onEnviada();
    },
  });

  const texto = textoDeLaOrden(orden);

  if (!alta.abierto) {
    return (
      <Boton variante="primario" onClick={alta.abrir}>
        Mandar
      </Boton>
    );
  }

  /*
   * `wa.me` sin número abre el selector de contactos, que sirve igual: el
   * mensaje va puesto y quien manda elige a quién. Con número va directo.
   */
  const whatsapp = `https://wa.me/${numeroParaWhatsapp(orden.supplierPhone)}?text=${encodeURIComponent(texto)}`;

  return (
    <Modal titulo={`Mandar la orden ${orden.number}`} onCerrar={alta.cerrar}>
      <div className="formulario">
        {orden.supplierPhone === null ? (
          <p className="aviso-caja">
            {orden.supplier} no tiene teléfono en su ficha, así que WhatsApp
            abrirá la lista de contactos con el mensaje ya escrito. Guardándole
            el número, la próxima vez va directo.
          </p>
        ) : (
          <p className="nota nota--pegada">
            Se le manda a{' '}
            <strong>
              {orden.supplierContact ?? orden.supplier} · {orden.supplierPhone}
            </strong>
            .
          </p>
        )}

        <pre className="texto-para-mandar">{texto}</pre>

        <div className="formulario__fila">
          <Boton
            onClick={() => {
              void navigator.clipboard?.writeText(texto).then(
                () => setCopiado(true),
                () => setCopiado(false),
              );
            }}
          >
            {copiado ? 'Copiado' : 'Copiar'}
          </Boton>
          <Boton onClick={() => window.print()}>Imprimir</Boton>
          <a
            className="boton boton--primario"
            href={whatsapp}
            target="_blank"
            rel="noreferrer noopener"
          >
            Abrir WhatsApp
          </a>
        </div>

        {marcar.isError ? <Fallo error={marcar.error} /> : null}

        <p className="nota-sm nota--pegada">
          {/*
           * El estado cambia cuando la persona dice que salió, no al abrir
           * WhatsApp: abrirlo y arrepentirse dejaría una orden marcada como
           * enviada que nadie mandó, y el proveedor no aparecería nunca.
           */}
          La orden queda en borrador hasta que confirmes que salió. Abrir
          WhatsApp no la marca: puede que lo cierres sin mandar nada.
        </p>

        <div className="formulario__fila">
          <Boton onClick={alta.cerrar}>Todavía no</Boton>
          <Boton
            variante="primario"
            onClick={() => marcar.mutate()}
            disabled={marcar.isPending}
          >
            {marcar.isPending ? 'Guardando…' : 'Ya la mandé'}
          </Boton>
        </div>
      </div>
    </Modal>
  );
}

/**
 * La orden en palabras, como se le escribe a un proveedor.
 *
 * Con asteriscos porque WhatsApp los lee como negrita, y sin tabla porque en un
 * teléfono una tabla se rompe. El total va como **referencial**: sale del
 * promedio del almacén, no de la lista de precios del proveedor, y presentarlo
 * como el importe a pagar sería empezar la relación con un número equivocado.
 */
function textoDeLaOrden(orden: PurchaseOrder): string {
  const lineas = orden.lines.map(
    (line, indice) =>
      `${indice + 1}. ${line.supply} — ${cantidad(line.ordered)} ${line.unit}`,
  );

  return [
    `*Orden de compra ${orden.number}*`,
    orden.supplierContact === null
      ? `Para: ${orden.supplier}`
      : `Para: ${orden.supplierContact} (${orden.supplier})`,
    '',
    ...lineas,
    '',
    orden.expectedAt === null
      ? 'Entrega: a coordinar'
      : `Entrega: ${orden.expectedAt}`,
    `Entregar en: ${orden.branch}`,
    `Referencial: S/ ${orden.total}`,
  ].join('\n');
}

// ── Proveedor ─────────────────────────────────────────────────────────────

/**
 * La ficha de un proveedor: para darlo de alta y para corregirlo.
 *
 * No había forma de editar uno: un teléfono que cambiaba obligaba a dar de
 * alta otro proveedor, y el viejo seguía recibiendo las órdenes por WhatsApp.
 */
export function FichaDeProveedor({
  proveedor,
  onGuardado = () => {},
}: {
  proveedor?: Supplier;
  onGuardado?: () => void;
}) {
  const alta = usarAlta(onGuardado);
  const editando = proveedor !== undefined;

  const [supplierId, setSupplierId] = useState(() => proveedor?.id ?? newOperationId());
  const [nombre, setNombre] = useState(proveedor?.name ?? '');
  const [ruc, setRuc] = useState(proveedor?.taxId ?? '');
  const [rubros, setRubros] = useState(proveedor?.suppliesSummary ?? '');
  const [dias, setDias] = useState(String(proveedor?.leadTimeDays ?? 2));
  const [pago, setPago] = useState(proveedor?.paymentTerms ?? '');
  const [minimo, setMinimo] = useState(proveedor?.minimumOrder ?? '');
  const [telefono, setTelefono] = useState(proveedor?.phone ?? '');
  const [contacto, setContacto] = useState(proveedor?.contactName ?? '');
  const [estado, setEstado] = useState<'preferred' | 'active' | 'observed'>(proveedor?.status ?? 'active');

  const guardar = useMutation({
    mutationFn: () =>
      almacen.saveSupplier({
        supplierId,
        name: nombre.trim(),
        // Media compra de un restaurante peruano es el mercado de la esquina,
        // que no da RUC. Se manda nulo en vez de una cadena vacía.
        taxId: ruc.trim() === '' ? null : ruc.trim(),
        suppliesSummary: rubros.trim() === '' ? null : rubros.trim(),
        leadTimeDays: Number.parseInt(dias, 10) || 1,
        phone: telefono.trim() === '' ? null : telefono.trim(),
        contactName: contacto.trim() === '' ? null : contacto.trim(),
        paymentTerms: pago.trim() === '' ? null : pago.trim(),
        minimumOrder: minimo.trim() === '' ? null : minimo.trim(),
        status: estado,
      }),
    onSuccess: () => {
      if (!editando) {
        // Todo: el teléfono y el contacto del anterior se quedaban puestos, y
        // el siguiente proveedor nacía con el WhatsApp de otro.
        setSupplierId(newOperationId());
        setNombre('');
        setRuc('');
        setRubros('');
        setDias('2');
        setPago('');
        setMinimo('');
        setTelefono('');
        setContacto('');
        setEstado('active');
      }
      alta.guardado();
    },
  });

  if (!alta.abierto) {
    return editando ? (
      <button type="button" className="accion-fila" onClick={alta.abrir}>
        Editar
      </button>
    ) : (
      <Boton variante="primario" onClick={alta.abrir}>
        Nuevo proveedor
      </Boton>
    );
  }

  return (
    <Modal titulo={editando ? `Editar ${proveedor.name}` : 'Nuevo proveedor'} onCerrar={alta.cerrar}>
      <div className="formulario">
        <div className="formulario__fila">
          <div className="campo">
            <Rotulo htmlFor="pnombre" ayuda="Cómo se conoce al proveedor en el local: el nombre de la empresa o del puesto del mercado.">Nombre</Rotulo>
            <input
              id="pnombre"
              placeholder="Frigorífico Andino"
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
            />
          </div>
          <div className="campo">
            <Rotulo htmlFor="ruc" ayuda="Los once dígitos del RUC, si da factura. El mercado de la esquina no tiene: déjalo vacío.">RUC</Rotulo>
            <input
              id="ruc"
              inputMode="numeric"
              placeholder="Once dígitos, o vacío"
              value={ruc}
              onChange={(e) => setRuc(e.target.value)}
            />
          </div>
        </div>

        <div className="campo">
          <Rotulo htmlFor="rubros" ayuda="Qué suele traer: «carnes rojas», «verduras». Ayuda a saber a quién pedirle qué.">Qué trae</Rotulo>
          <input
            id="rubros"
            placeholder="Carnes rojas, cortes porcionados"
            value={rubros}
            onChange={(e) => setRubros(e.target.value)}
          />
        </div>

        <div className="formulario__fila">
          <div className="campo">
            <Rotulo htmlFor="dias" ayuda="Cuántos días tarda en llegar desde que se le pide. «Qué comprar» calcula la fecha de entrega con esto.">Días de entrega</Rotulo>
            <input
              id="dias"
              inputMode="numeric"
              value={dias}
              onChange={(e) => setDias(e.target.value)}
            />
          </div>
          <div className="campo">
            <Rotulo htmlFor="prov-telefono" ayuda="El número al que se le mandan los pedidos por WhatsApp. Un celular de 9 dígitos basta; el 51 se pone solo.">Teléfono</Rotulo>
            <input
              id="prov-telefono"
              inputMode="tel"
              placeholder="987 654 321"
              value={telefono}
              onChange={(e) => setTelefono(e.target.value)}
            />
            <p className="nota-sm nota--pegada">
              {/*
               * Sin formato a propósito: el celular del mercado y el fijo con
               * anexo del distribuidor son los dos válidos, y exigir uno solo
               * dejaría fuera al proveedor que más se usa.
               */}
              A donde se le manda la orden por WhatsApp.
            </p>
          </div>
          <div className="campo">
            <Rotulo htmlFor="prov-contacto" ayuda="La persona que atiende los pedidos, para saludarla por su nombre.">Con quién se habla</Rotulo>
            <input
              id="prov-contacto"
              placeholder="Marisol, pedidos"
              value={contacto}
              onChange={(e) => setContacto(e.target.value)}
            />
          </div>
        </div>

        <div className="formulario__fila">
          <div className="campo">
            <Rotulo htmlFor="pago" ayuda="Cómo se le paga: contado, crédito a 15 días… Para tenerlo a mano al recibir la factura.">Condición de pago</Rotulo>
            <input
              id="pago"
              placeholder="Crédito 15 días"
              value={pago}
              onChange={(e) => setPago(e.target.value)}
            />
          </div>
          <div className="campo">
            <Rotulo htmlFor="pminimo" ayuda="En dinero, no en cantidad: cuántos soles hay que pedirle para que despache. Si el pedido no llega, conviene juntar más cosas.">Pedido mínimo (S/)</Rotulo>
            <input
              id="pminimo"
              inputMode="decimal"
              placeholder="600.00 (soles)"
              value={minimo}
              onChange={(e) => setMinimo(e.target.value)}
            />
          </div>
        </div>

        <p className="versal" style={{ marginBottom: 'var(--hueco-sm)' }}>
          Cómo se trabaja con él
        </p>
        <div className="opciones">
          {(
            [
              ['preferred', 'Preferido', 'Es al que se le compra primero'],
              ['active', 'Activo', 'Se le compra con normalidad'],
              ['observed', 'Observado', 'Falla entregas y hay que vigilarlo'],
            ] as const
          ).map(([id, texto, apoyo]) => (
            <button
              key={id}
              type="button"
              className={`opcion ${estado === id ? 'opcion--activa' : ''}`}
              onClick={() => setEstado(id)}
              aria-pressed={estado === id}
            >
              <span>
                <strong>{texto}</strong>
                <span className="opcion__apoyo">
                  {apoyo}
                </span>
              </span>
            </button>
          ))}
        </div>

        {guardar.isError ? <Fallo error={guardar.error} /> : null}

        <div className="formulario__fila">
          <Boton onClick={alta.cerrar}>Cancelar</Boton>
          <Boton
            variante="primario"
            onClick={() => guardar.mutate()}
            disabled={nombre.trim() === '' || guardar.isPending}
          >
            {guardar.isPending ? 'Guardando…' : editando ? 'Guardar cambios' : 'Dar de alta'}
          </Boton>
        </div>
      </div>
    </Modal>
  );
}

// ── Producción ────────────────────────────────────────────────────────────

/**
 * Una sub-receta: qué insumo sale, cuánto rinde la tanda y qué lleva.
 *
 * La pantalla de producción decía «se les carga su receta» y no había dónde:
 * la ficha de receta solo aceptaba platos de la carta. Sirve para crearla y
 * para corregirla, y al corregir trae lo que ya tenía.
 */
export function FichaDeProduccion({
  produccion,
  onGuardado = () => {},
}: {
  produccion?: Production;
  onGuardado?: () => void;
}) {
  const alta = usarAlta(onGuardado);
  const editando = produccion !== undefined;

  const insumos = useQuery({
    queryKey: ['insumos'],
    queryFn: almacen.supplies,
    enabled: alta.abierto,
  });

  const [producesSupplyId, setProducesSupplyId] = useState(produccion?.producesSupplyId ?? '');
  const [rinde, setRinde] = useState(produccion === undefined ? '' : produccion.yieldQuantity.replace('.', ','));
  const [merma, setMerma] = useState(String(produccion?.wastePercentage ?? 0).replace('.', ','));
  const [lineas, setLineas] = useState<Record<string, string>>(() =>
    Object.fromEntries((produccion?.lines ?? []).map((linea) => [linea.supplyId, linea.quantity.replace('.', ',')])),
  );
  const [busqueda, setBusqueda] = useState('');

  const producto = (insumos.data ?? []).find((insumo) => insumo.id === producesSupplyId);

  const guardar = useMutation({
    mutationFn: () =>
      almacen.saveRecipe({
        // Una producción por insumo: el servidor guarda sobre la que ya
        // hubiera de ese insumo, así que el identificador no la duplica.
        recipeId: produccion?.id ?? newOperationId(),
        producesSupplyId,
        name: producto?.name ?? produccion?.name ?? 'Producción',
        yieldQuantity: rinde,
        wastePercentage: merma.trim() === '' ? 0 : importe(merma, 'La merma'),
        lines: Object.entries(lineas)
          .filter(([, valor]) => valor.trim() !== '')
          .map(([supplyId, quantity]) => ({ supplyId, quantity })),
      }),
    onSuccess: () => {
      if (!editando) {
        setProducesSupplyId('');
        setRinde('');
        setMerma('0');
        setLineas({});
      }
      alta.guardado();
    },
  });

  const puestos = Object.values(lineas).filter((v) => v.trim() !== '').length;
  const buscado = busqueda.trim().toLowerCase();
  const visibles = (insumos.data ?? []).filter(
    (insumo) =>
      insumo.id !== producesSupplyId &&
      ((lineas[insumo.id] ?? '').trim() !== '' ||
        buscado === '' ||
        insumo.name.toLowerCase().includes(buscado) ||
        insumo.sku.toLowerCase().includes(buscado)),
  );

  if (!alta.abierto) {
    return editando ? (
      <button type="button" className="accion-fila" onClick={alta.abrir}>
        Editar receta
      </button>
    ) : (
      <Boton variante="primario" onClick={alta.abrir}>
        Nueva producción
      </Boton>
    );
  }

  return (
    <Modal titulo={editando ? `Editar ${produccion.name}` : 'Nueva producción'} onCerrar={alta.cerrar}>
      <div className="formulario">
        <div className="formulario__fila">
          <div className="campo">
            <Rotulo htmlFor="produce" ayuda="El insumo que sale de esta preparación, como la leche de tigre. Primero se da de alta en Insumos.">Qué sale</Rotulo>
            <Buscable
              id="produce"
              valor={producesSupplyId}
              onCambio={setProducesSupplyId}
              deshabilitado={editando}
              placeholder="Busca lo que se produce…"
              opciones={(insumos.data ?? []).map((insumo) =>
                opcionDeInsumo(insumo, `se cuenta en ${insumo.unit}`),
              )}
            />
            <p className="nota-sm nota--pegada">
              Es un insumo más: se da de alta en Insumos, con lo que dura, y aquí se dice cómo se hace.
            </p>
          </div>
          <div className="campo">
            <Rotulo htmlFor="rinde" ayuda="Cuánto sale de una tanda, en la unidad en que se cuenta lo producido.">Rinde por tanda{producto === undefined ? '' : ` (${producto.unit})`}</Rotulo>
            <input id="rinde" inputMode="decimal" placeholder="6" value={rinde} onChange={(e) => setRinde(e.target.value)} />
          </div>
          <div className="campo">
            <Rotulo htmlFor="prod-merma" ayuda="Lo que se pierde al preparar. Se descuenta de los ingredientes y sube el costo de lo producido.">Merma (%)</Rotulo>
            <input id="prod-merma" inputMode="decimal" value={merma} onChange={(e) => setMerma(e.target.value)} />
          </div>
        </div>

        <p className="aviso-caja">
          La merma sale del almacén al producir, no solo entra en el costo: lo que se queda en la olla
          también se sacó del estante.
        </p>

        <div className="buscador-de-lista">
          <p className="versal">Qué lleva, por tanda</p>
          <input
            className="buscador"
            type="search"
            aria-label="Buscar insumo"
            placeholder="Buscar insumo…"
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
          />
        </div>
        <ul className="lista">
          {visibles.map((insumo) => (
            <li key={insumo.id} className="lista__fila">
              <div className="lista__texto">
                <strong>{insumo.name}</strong>
                <span className="apagado mono">
                  {insumo.unit} · quedan {cantidad(insumo.quantity)}
                </span>
              </div>
              <input
                className="celda-input"
                inputMode="decimal"
                aria-label={`Cuánto lleva de ${insumo.name}`}
                placeholder="0"
                value={lineas[insumo.id] ?? ''}
                onChange={(e) => setLineas((previo) => ({ ...previo, [insumo.id]: e.target.value }))}
              />
            </li>
          ))}
        </ul>

        {guardar.isError ? <Fallo error={guardar.error} /> : null}

        <div className="formulario__fila">
          <Boton onClick={alta.cerrar}>Cancelar</Boton>
          <Boton
            variante="primario"
            onClick={() => guardar.mutate()}
            disabled={producesSupplyId === '' || rinde.trim() === '' || puestos === 0 || guardar.isPending}
          >
            {guardar.isPending ? 'Guardando…' : `Guardar con ${contados(puestos, 'ingrediente', 'ingredientes')}`}
          </Boton>
        </div>
      </div>
    </Modal>
  );
}

// ── Ajuste manual ─────────────────────────────────────────────────────────

export function AjusteManual({ onGuardado = () => {} }: { onGuardado?: () => void }) {
  const alta = usarAlta(onGuardado);
  const vocabulario = useVocabulario();
  const insumos = useQuery({
    queryKey: ['insumos'],
    queryFn: almacen.supplies,
    enabled: alta.abierto,
  });

  const [supplyId, setSupplyId] = useState('');
  const [contado, setContado] = useState('');
  const [motivo, setMotivo] = useState('');

  const elegido = (insumos.data ?? []).find((i) => i.id === supplyId);
  const [operationId, renovarOperacion] = usarOperacion();

  const guardar = useMutation({
    mutationFn: () =>
      almacen.adjust({
        operationId,
        supplyId,
        counted: contado,
        reason: motivo.trim(),
      }),
    onSuccess: () => {
      setContado('');
      setMotivo('');
      renovarOperacion();
      alta.guardado();
    },
  });

  if (!alta.abierto) {
    return (
      <Boton variante="primario" onClick={alta.abrir}>
        Ajuste manual
      </Boton>
    );
  }

  // Con la coma peruana: «9,5» es nueve y medio, no nueve.
  const leido = leerCantidad(contado);
  const diferencia =
    elegido === undefined || Number.isNaN(leido)
      ? null
      : Math.round((leido - Number.parseFloat(elegido.quantity)) * 1000) / 1000;

  return (
    <Modal titulo="Ajuste manual" onCerrar={alta.cerrar}>
      <div className="formulario">
        <p className="aviso-caja">
          Corrige el stock sin abrir un conteo entero. Deja el mismo rastro: un
          movimiento de ajuste con su motivo y tu nombre. No todo descuadre se
          descubre contando.
        </p>

        <div className="campo">
          <Rotulo htmlFor="ainsumo" ayuda={`El ${vocabulario.insumo} cuyo stock no coincide con lo que hay de verdad.`}>{mayuscula(vocabulario.insumo)}</Rotulo>
          <Buscable
            id="ainsumo"
            valor={supplyId}
            onCambio={setSupplyId}
            placeholder={`Busca el ${vocabulario.insumo} por nombre o código…`}
            opciones={(insumos.data ?? []).map((i) =>
              opcionDeInsumo(i, `el sistema dice ${cantidad(i.quantity)} ${i.unit}`),
            )}
          />
        </div>

        <div className="formulario__fila">
          <div className="campo">
            <Rotulo htmlFor="contado" ayuda="Lo que hay físicamente en el estante ahora. El sistema calcula la diferencia y la deja en el historial.">
              Cuánto hay de verdad {elegido === undefined ? '' : `(${elegido.unit})`}
            </Rotulo>
            <input
              id="contado"
              inputMode="decimal"
              value={contado}
              onChange={(e) => setContado(e.target.value)}
            />
          </div>
          <div className="campo">
            <label>Diferencia</label>
            <p
              className={`mono ${
                diferencia === null || diferencia === 0
                  ? 'apagado'
                  : diferencia < 0
                    ? 'negativo'
                    : 'positivo'
              }`}
              style={{ margin: 0, padding: 'var(--hueco-md) 0' }}
            >
              {diferencia === null
                ? '—'
                : `${diferencia > 0 ? '+' : ''}${cantidad(String(diferencia))}`}
            </p>
          </div>
        </div>

        <div className="campo">
          <Rotulo htmlFor="motivo" ayuda="Por qué cambia el stock. Es obligatorio: un ajuste sin motivo es un número que cambió y nadie sabe por qué.">Por qué</Rotulo>
          {/*
           * Obligatorio. Un ajuste sin motivo es exactamente el agujero que
           * este módulo existe para no tener: un número que cambió y nadie
           * sabe por qué.
           */}
          <input
            id="motivo"
            placeholder="Se rompió un paquete al abrirlo"
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
          />
        </div>

        {guardar.isError ? <Fallo error={guardar.error} /> : null}

        <div className="formulario__fila">
          <Boton onClick={alta.cerrar}>Cancelar</Boton>
          <Boton
            variante="primario"
            onClick={() => guardar.mutate()}
            disabled={
              supplyId === '' ||
              contado.trim() === '' ||
              motivo.trim().length < 3 ||
              guardar.isPending
            }
          >
            {guardar.isPending ? 'Ajustando…' : 'Ajustar el stock'}
          </Boton>
        </div>
      </div>
    </Modal>
  );
}

// ── Retirar y devolver ────────────────────────────────────────────────────

/**
 * Retirar del catálogo, con un segundo toque.
 *
 * No borra: lo retirado deja de salir en las listas y conserva su historia.
 * Si el servidor dice que no —un insumo con stock, un proveedor con una orden
 * viva— lo dice aquí mismo, con el porqué.
 */
export function Retirar({ nombre, retirar }: { nombre: string; retirar: () => Promise<void> }) {
  const clientes = useQueryClient();
  const [confirma, setConfirma] = useState(false);

  const hacer = useMutation({
    mutationFn: retirar,
    onSuccess: () => {
      setConfirma(false);
      void clientes.invalidateQueries();
    },
  });

  if (!confirma) {
    return (
      <button type="button" className="accion-fila" onClick={() => setConfirma(true)}>
        Retirar
      </button>
    );
  }

  return (
    <Modal titulo={`Retirar ${nombre}`} onCerrar={() => setConfirma(false)}>
      <div className="formulario">
        <p className="aviso-caja">
          Deja de salir en las listas y en los desplegables. Su historia se queda en el kardex, y se
          puede devolver al catálogo desde «Retirados».
        </p>
        {hacer.isError ? <Fallo error={hacer.error} /> : null}
        <div className="formulario__fila">
          <Boton onClick={() => setConfirma(false)}>Cancelar</Boton>
          <Boton variante="peligro" onClick={() => hacer.mutate()} disabled={hacer.isPending}>
            {hacer.isPending ? 'Retirando…' : 'Retirar'}
          </Boton>
        </div>
      </div>
    </Modal>
  );
}

/** Lo retirado, para devolverlo al catálogo si fue un error. */
export function Retirados({ que }: { que: 'insumos' | 'proveedores' }) {
  const clientes = useQueryClient();
  const [abierto, setAbierto] = useState(false);
  const vocabulario = useVocabulario();
  const retirados = useQuery({ queryKey: ['retirados'], queryFn: almacen.retired, enabled: abierto });

  const devolver = useMutation({
    mutationFn: (id: string) => (que === 'insumos' ? almacen.restoreSupply(id) : almacen.restoreSupplier(id)),
    onSuccess: () => void clientes.invalidateQueries(),
  });

  if (!abierto) {
    return <Boton onClick={() => setAbierto(true)}>Retirados</Boton>;
  }

  const lista =
    que === 'insumos'
      ? (retirados.data?.supplies ?? []).map((i) => ({ id: i.id, nombre: `${i.sku} · ${i.name}`, cuando: i.retiredAt }))
      : (retirados.data?.suppliers ?? []).map((p) => ({ id: p.id, nombre: p.name, cuando: p.retiredAt }));

  return (
    <Modal titulo={`${que === 'insumos' ? mayuscula(vocabulario.insumos) : 'Proveedores'} retirados`} onCerrar={() => setAbierto(false)}>
      <div className="formulario">
        {retirados.isError ? <Fallo error={retirados.error} /> : null}
        {lista.length === 0 && !retirados.isLoading ? (
          <p className="nota nota--pegada">No hay nada retirado.</p>
        ) : (
          <ul className="lista">
            {lista.map((fila) => (
              <li key={fila.id} className="lista__fila">
                <div className="lista__texto">
                  <strong>{fila.nombre}</strong>
                  <span className="apagado">Retirado el {new Date(fila.cuando).toLocaleDateString('es-PE', { timeZone: 'America/Lima' })}</span>
                </div>
                <Boton onClick={() => devolver.mutate(fila.id)} disabled={devolver.isPending}>
                  Devolver
                </Boton>
              </li>
            ))}
          </ul>
        )}
        {devolver.isError ? <Fallo error={devolver.error} /> : null}
      </div>
    </Modal>
  );
}
